import type { FC, SetStateAction } from "react"
import React, { useState, useMemo, useCallback, useEffect } from "react"

import { useRecoilValue } from "recoil"

import type { IntelligentBaselineResponseDto } from "~/api/services/genability/genability.types"
import type { UseProposal } from "~/containers/Proposal/state/proposal/proposal-context"
import { useProposalContext } from "~/containers/Proposal/state/proposal/proposal-context"
import { calculatedUtilityRateState } from "~/containers/Proposal/state/utility-costs-and-savings/utility-costs-and-savings-state"
import { useCanEditProposal } from "~/hooks/useCanEditProposal"

import { AnnualUsageForm } from "./components/AnnualUsageForm"
import { BillEntryForm } from "./components/BillEntryForm"
import { ConsumptionSource } from "./components/ConsumptionSource"
import { EntryModeSelection } from "./components/EntryModeSelection"
import { GridView } from "./components/GridView"
import {
  MissingUtilityWarning,
  EmptyDataMessage,
  ErrorMessage,
} from "./components/Messages"
import type {
  MonthData,
  ViewMode,
  DisplayUnit,
  BillEntryData,
  AnnualUsageData,
} from "./components/types"
import { useAutoGenerateEstimates } from "./hooks/useAutoGenerateEstimates"
import { useEnergyConsumptionInitialization } from "./hooks/useEnergyConsumptionInitialization"
import { useEnergyEstimation } from "./hooks/useEnergyEstimation"
import { useEstimateSource } from "./hooks/useEstimateSource"
import { EnergyConsumptionContainer } from "./styles"
import { SetupMenuFormFooter } from "../../SetupMenuFooter"

export const EnergyConsumptionForm: FC<{
  proposal: ReturnType<UseProposal>
  monthlyData: MonthData[]
  setMonthlyData: React.Dispatch<React.SetStateAction<MonthData[]>>
  isProposalSaving?: boolean
  accountId?: string
  calculatedUtilityRate: number
  onViewModeChange?: (mode: ViewMode) => void
}> = ({
  proposal,
  monthlyData,
  setMonthlyData,
  isProposalSaving,
  accountId,
  calculatedUtilityRate,
  onViewModeChange,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>(`grid`)
  const [displayUnit, setDisplayUnit] = useState<DisplayUnit>(`kWh`)
  const [error, setError] = useState<string | null>(null)
  const [previousMonthlyData, setPreviousMonthlyData] = useState<
    MonthData[] | null
  >(null)
  const [previousBanner, setPreviousBanner] = useState<string | null>(null)
  const canEdit = useCanEditProposal()

  const utilityRateDisplay = useMemo(() => {
    if (calculatedUtilityRate > 0) {
      return `$${calculatedUtilityRate.toFixed(2)}/kWh`
    }
    return null
  }, [calculatedUtilityRate])

  const {
    estimateSourceText: baseEstimateSourceText,
    setAnnualSource,
    setBillSource,
    setDefaultSource,
    resetSource,
  } = useEstimateSource(utilityRateDisplay)

  const {
    isLoading,
    estimateFromAnnual,
    estimateFromBill,
    estimateFromMonthlyInputs,
  } = useEnergyEstimation(accountId, (data) => {
    if (data.type === `annual` && data.annualUsage) {
      setAnnualSource(data.annualUsage)
    } else if (
      data.type === `bill` &&
      data.billAmount &&
      data.billStartMonth !== undefined
    ) {
      setBillSource(
        data.billAmount,
        data.billStartMonth,
        data.billEndMonth ?? data.billStartMonth,
      )
    } else {
      setDefaultSource()
    }
  })

  const { changeLocal, setConsumptionInputMode, setConsumptionEntryUnits } =
    proposal

  const storedBanner = proposal.local.smartEstimateBanner ?? null

  const syncMonthlyDataToProposal = useCallback(
    (data: MonthData[]) => {
      const monthlyEnergyCost = data.map((month) => month.costValue ?? 0)
      const monthlyEnergyUse = data.map((month) => month.kwhValue ?? 0)
      const monthlyEnergyManualFlags = data.map(
        (month) => month.source === `user`,
      )

      // A manual flag alone cannot attest kWh provenance — a month entered in
      // dollars keeps a provider-modeled kWh value. Record the entered units
      // so the Consumption Profile preserves accurate provenance.
      setConsumptionEntryUnits(
        data.map((month) => {
          if (month.source !== `user`) return null
          if (month.manualEntryUnit === `$`) return `dollars`
          if (month.manualEntryUnit === `kWh`) return `kwh`

          return null
        }),
      )

      changeLocal({
        monthlyEnergyCost,
        monthlyEnergyUse,
        monthlyEnergyManualFlags,
      })
    },
    [changeLocal, setConsumptionEntryUnits],
  )

  const updateMonthlyData = useCallback(
    (updater: SetStateAction<MonthData[]>) => {
      setMonthlyData((prev) => {
        const next =
          typeof updater === `function`
            ? (updater as (value: MonthData[]) => MonthData[])(prev)
            : updater

        if (next !== prev) {
          syncMonthlyDataToProposal(next)
        }

        return next
      })
    },
    [setMonthlyData, syncMonthlyDataToProposal],
  )

  // Helper function to update grid with API response (eliminates race conditions)
  const updateGridWithApiResponse = useCallback(
    (apiResponse: IntelligentBaselineResponseDto, forceOverwrite = false) => {
      if (apiResponse?.monthlyEstimates) {
        updateMonthlyData((prev) =>
          prev.map((item) => {
            const estimate = apiResponse.monthlyEstimates.find(
              (e) => e.month === item.index,
            )

            if (estimate) {
              // When coming from "Start Over" flow, always overwrite all values
              if (forceOverwrite || viewMode === `selectEntry`) {
                return {
                  ...item,
                  costValue: estimate.amount,
                  kwhValue: estimate.usage,
                  source: `estimated` as const,
                  manualEntryUnit: null,
                }
              }

              // For user-edited cells, update the complementary value from API but keep user input
              if (item.source === `user`) {
                if (item.manualEntryUnit === `$`) {
                  return {
                    ...item,
                    costValue: item.costValue,
                    kwhValue: estimate.usage,
                  }
                }

                if (item.manualEntryUnit === `kWh`) {
                  return {
                    ...item,
                    costValue: estimate.amount,
                    kwhValue: item.kwhValue,
                  }
                }

                return {
                  ...item,
                  // Always populate both values using API data for the non-user-edited field
                  costValue:
                    item.costValue !== null ? item.costValue : estimate.amount,
                  kwhValue:
                    item.kwhValue !== null ? item.kwhValue : estimate.usage,
                }
              }

              // For estimated values, update both fields
              return {
                ...item,
                costValue: estimate.amount,
                kwhValue: estimate.usage, // Use kWh from Genability instead of calculating
                source: `estimated` as const,
                manualEntryUnit: null,
              }
            }

            return item
          }),
        )
      }
    },
    [updateMonthlyData, viewMode],
  )

  const hasUserEdits = useMemo(
    () => monthlyData.some((month) => month.source === `user`),
    [monthlyData],
  )

  // Determine banner text to display/persist
  const estimateSourceText = useMemo(() => {
    const rateText = utilityRateDisplay || `selected rate`

    if (hasUserEdits) {
      // Sum all 12 months of kWh values (custom + estimated)
      const totalKwh = monthlyData.reduce(
        (sum, month) => sum + (month.kwhValue ?? 0),
        0,
      )
      const formattedTotalKwh = totalKwh.toLocaleString(undefined, {
        maximumFractionDigits: 0,
      })
      return `${formattedTotalKwh} kWh annually with ${rateText}`
    }

    if (baseEstimateSourceText) {
      return baseEstimateSourceText
    }

    return storedBanner
  }, [
    hasUserEdits,
    baseEstimateSourceText,
    storedBanner,
    utilityRateDisplay,
    monthlyData,
  ])

  useEffect(() => {
    const nextBanner = estimateSourceText ?? null

    if (nextBanner !== storedBanner) {
      changeLocal({ smartEstimateBanner: nextBanner })
    }
  }, [changeLocal, estimateSourceText, storedBanner])

  useEffect(() => {
    onViewModeChange?.(viewMode)
  }, [onViewModeChange, viewMode])

  // Handle cell value changes
  const handleCellChange = useCallback(
    (monthIndex: number, value: number | null) => {
      if (!canEdit) return

      setConsumptionInputMode(`monthly_usage`)
      updateMonthlyData((prev) =>
        prev.map((item, idx) =>
          idx === monthIndex
            ? displayUnit === `$`
              ? {
                  ...item,
                  costValue: value,
                  source: value !== null ? `user` : `empty`,
                  manualEntryUnit: value !== null ? `$` : null,
                }
              : {
                  ...item,
                  kwhValue: value,
                  source: value !== null ? `user` : `empty`,
                  manualEntryUnit: value !== null ? `kWh` : null,
                }
            : item,
        ),
      )
    },
    [canEdit, displayUnit, setConsumptionInputMode, updateMonthlyData],
  )

  const handleStartOver = useCallback(() => {
    if (!canEdit) return

    // Save current data before clearing
    setPreviousMonthlyData([...monthlyData])
    setPreviousBanner(storedBanner)
    resetSource()
    setViewMode(`selectEntry`)
    setError(null)
    // Clear all monthly data to ensure a clean slate
    updateMonthlyData((prev) =>
      prev.map((item) => ({
        ...item,
        costValue: null,
        kwhValue: null,
        source: `empty` as const,
        manualEntryUnit: null,
      })),
    )
    // Also clear proposal data to prevent auto-generation from triggering
    changeLocal({
      annualEnergyUse: 0,
      monthlyEnergyCost: new Array(12).fill(0),
      monthlyEnergyUse: new Array(12).fill(0),
      monthlyEnergyManualFlags: new Array(12).fill(false),
      smartEstimateBanner: null,
    })
  }, [
    canEdit,
    updateMonthlyData,
    changeLocal,
    monthlyData,
    resetSource,
    storedBanner,
  ])

  const handleCancelStartOver = useCallback(() => {
    // Restore the previous data if user cancels from entry mode selection
    if (previousMonthlyData) {
      updateMonthlyData(previousMonthlyData)
      setPreviousMonthlyData(null)
    }
    if (previousBanner !== null) {
      changeLocal({ smartEstimateBanner: previousBanner })
      setPreviousBanner(null)
    }
    setViewMode(`grid`)
  }, [previousMonthlyData, updateMonthlyData, previousBanner, changeLocal])

  // Handle Annual Usage submission
  const handleAnnualUsageSubmit = useCallback(
    async (data: AnnualUsageData) => {
      if (!canEdit) return

      setError(null)
      try {
        const annualUsage = parseFloat(data.annualUsage)
        const apiResponse = await estimateFromAnnual(annualUsage)
        setConsumptionInputMode(`annual_usage`)
        updateGridWithApiResponse(apiResponse, true)
        changeLocal({ annualEnergyUse: apiResponse.totalAnnual })
        setPreviousMonthlyData(null) // Clear saved data on successful submission
        setViewMode(`grid`)
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : `Failed to generate annual usage estimates`
        setError(message)
        console.error(`Failed to generate annual usage estimates:`, error)
      }
    },
    [
      canEdit,
      changeLocal,
      estimateFromAnnual,
      setConsumptionInputMode,
      updateGridWithApiResponse,
    ],
  )

  const handleBillEntrySubmit = useCallback(
    async (data: BillEntryData) => {
      if (!canEdit) return

      setError(null)
      try {
        const startDate = new Date(data.startDate)
        const endDate = new Date(data.endDate)
        const billStartMonth = startDate.getMonth()
        const billEndMonth = endDate.getMonth()
        const billAmount = parseFloat(data.billAmount)
        const apiResponse = await estimateFromBill(
          billAmount,
          billStartMonth,
          billEndMonth,
        )
        setConsumptionInputMode(`bill`, {
          bills: [{ month: billStartMonth, amount: billAmount }],
        })
        updateGridWithApiResponse(apiResponse, true)
        changeLocal({ annualEnergyUse: apiResponse.totalAnnual })
        setPreviousMonthlyData(null) // Clear saved data on successful submission
        setViewMode(`grid`)
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : `Failed to generate bill estimates`
        setError(message)
        console.error(`Failed to generate bill estimates:`, error)
      }
    },
    [
      canEdit,
      changeLocal,
      estimateFromBill,
      setConsumptionInputMode,
      updateGridWithApiResponse,
    ],
  )

  const handleRefineEstimates = useCallback(async () => {
    if (!canEdit || !hasUserEdits) return

    setError(null)
    try {
      const apiResponse = await estimateFromMonthlyInputs(
        monthlyData,
        displayUnit,
      )
      updateGridWithApiResponse(apiResponse)
    } catch (error) {
      const message =
        error instanceof Error ? error.message : `Failed to refine estimates`
      setError(message)
      console.error(`Failed to refine estimates:`, error)
    }
  }, [
    canEdit,
    hasUserEdits,
    monthlyData,
    displayUnit,
    estimateFromMonthlyInputs,
    updateGridWithApiResponse,
  ])

  useAutoGenerateEstimates({
    accountId,
    proposal,
    monthlyData,
    estimateFromAnnual,
    estimateFromBill,
    viewMode,
    setEstimateSourceData: (data) => {
      if (data.type === `annual` && data.annualUsage) {
        setAnnualSource(data.annualUsage)
      } else if (
        data.type === `bill` &&
        data.billAmount &&
        data.billStartMonth !== undefined
      ) {
        setBillSource(
          data.billAmount,
          data.billStartMonth,
          data.billEndMonth ?? data.billStartMonth,
        )
      } else {
        setDefaultSource()
      }
    },
    onError: setError,
  })

  const showEmptyDataMessage = useMemo(
    () =>
      viewMode === `grid` &&
      !monthlyData.some(
        (month) => (month.costValue || 0) > 0 || (month.kwhValue || 0) > 0,
      ),
    [viewMode, monthlyData],
  )

  return (
    <EnergyConsumptionContainer isProposalSaving={isProposalSaving}>
      {!proposal.remote.utilityRate && <MissingUtilityWarning />}

      <ConsumptionSource proposalId={proposal.remote.id} />

      {viewMode === `grid` && (
        <GridView
          monthlyData={monthlyData}
          displayUnit={displayUnit}
          onCellChange={handleCellChange}
          onStartOver={handleStartOver}
          onRefineEstimates={handleRefineEstimates}
          onToggleUnit={setDisplayUnit}
          hasUserEdits={hasUserEdits}
          estimateSource={estimateSourceText}
          isRefining={isLoading}
          canEdit={canEdit}
        />
      )}

      {viewMode === `selectEntry` && (
        <EntryModeSelection
          onSelectMode={setViewMode}
          onCancel={handleCancelStartOver}
          canEdit={canEdit}
        />
      )}

      {viewMode === `annualForm` && (
        <AnnualUsageForm
          onSubmit={handleAnnualUsageSubmit}
          onCancel={() => setViewMode(`selectEntry`)}
          isLoading={isLoading}
          canEdit={canEdit}
        />
      )}

      {viewMode === `billForm` && (
        <BillEntryForm
          onSubmit={handleBillEntrySubmit}
          onCancel={() => setViewMode(`selectEntry`)}
          isLoading={isLoading}
          canEdit={canEdit}
        />
      )}

      {error && <ErrorMessage message={error} />}
      {showEmptyDataMessage && <EmptyDataMessage />}
    </EnergyConsumptionContainer>
  )
}

export const EnergyConsumption: FC<{
  onSubmit?: VoidFunction
  onCancel?: VoidFunction
  shouldNavigateOnSuccess?: boolean
}> = ({ onSubmit, onCancel, shouldNavigateOnSuccess = true }) => {
  const proposal = useProposalContext()
  const accountId = proposal.local?.site?.genabilityAccountId || undefined
  const calculatedUtilityRate = useRecoilValue(calculatedUtilityRateState)
  const [currentViewMode, setCurrentViewMode] = useState<ViewMode>(`grid`)

  const { monthlyData, setMonthlyData } = useEnergyConsumptionInitialization(
    proposal,
    accountId,
  )
  const proposalIsDirty = proposal.isDirty

  const hasValidData = useMemo(() => {
    // Check stored proposal data
    const hasStoredData =
      (proposal.local.annualEnergyUse || 0) > 0 ||
      proposal.local.monthlyEnergyCost?.some((cost) => cost > 0) ||
      proposal.local.monthlyEnergyUse?.some((usage) => usage > 0)

    // Check current grid data (user edits or API estimates)
    const hasGridData = monthlyData.some(
      (month) => (month.costValue || 0) > 0 || (month.kwhValue || 0) > 0,
    )

    return hasStoredData || hasGridData
  }, [
    proposal.local.annualEnergyUse,
    proposal.local.monthlyEnergyCost,
    proposal.local.monthlyEnergyUse,
    monthlyData,
  ])

  const canSave = isEnergyConsumptionSaveEnabled(currentViewMode, hasValidData)
  const hasUnsavedChanges = useMemo(
    () => canSave && proposalIsDirty,
    [canSave, proposalIsDirty],
  )

  const handleSave = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      // Data is already synced to proposal when monthlyData changes
      onSubmit?.()
    },
    [onSubmit],
  )

  return (
    <form id="energyConsumption">
      <EnergyConsumptionForm
        proposal={proposal}
        monthlyData={monthlyData}
        setMonthlyData={setMonthlyData}
        isProposalSaving={proposal.isLoading}
        accountId={accountId}
        calculatedUtilityRate={calculatedUtilityRate}
        onViewModeChange={setCurrentViewMode}
      />
      <SetupMenuFormFooter
        entitiesToUpdate={[proposal]}
        isLoading={proposal.isLoading}
        isDisabled={!canSave}
        isDirty={hasUnsavedChanges}
        onSubmit={handleSave}
        onCancel={onCancel}
        shouldNavigateOnSuccess={shouldNavigateOnSuccess}
      />
    </form>
  )
}

export const isEnergyConsumptionSaveEnabled = (
  viewMode: ViewMode,
  hasValidData: boolean,
): boolean => viewMode === `grid` && hasValidData
